import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getActivities } from '../../data/portfolio';
import ActivityTable from '../portfolio/ActivityTable';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import { Skeleton } from '../portfolio/Loading';
import { useT } from '../../i18n';

// Transaction history.
//
// Its own page and its own request rather than a section of /holdings: the
// holdings snapshot is cached whole and read on every visit, and this is
// unbounded history most readers never open.
//
// Fidelity only at present. IBKR is read from a Flex statement whose query is
// configured for positions and NAV; its Trades section would normalise into the
// same shape, but turning it on is a change to the query at IBKR rather than
// to this code. Said on the page rather than left for the reader to infer from
// an unexplained absence.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

export default function Transactions() {
    const { t } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // StrictMode double-invokes effects in development. The flag stops a
        // late response from a discarded mount overwriting the live one.
        let cancelled = false;
        getActivities()
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setState(LOAD_STATE.READY);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setState(LOAD_STATE.ERROR);
            });
        return () => { cancelled = true; };
    }, []);

    const activities = data?.activities ?? [];
    const currency = activities.find((a) => a.currency)?.currency ?? null;

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader title={t('activity.title')} />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={12} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">
                        {error?.message || t('activity.error')}
                    </p>
                )}

                {state === LOAD_STATE.READY && (
                    <>
                        {data.warnings?.length > 0 && (
                            <ul className="holdings__warnings">
                                {data.warnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </ul>
                        )}

                        {activities.length === 0 ? (
                            <div className="holdings__empty">
                                <p className="desc">
                                    {t('activity.emptyBefore')}{' '}
                                    <Link to="/holdings/settings">{t('activity.emptyLink')}</Link>
                                    {t('activity.emptyAfter')}
                                </p>
                            </div>
                        ) : (
                            <>
                                <ActivityTable activities={activities} currency={currency} />
                            </>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
